import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import timeAgo from './timeAgo';
import Tooltip from './Tooltip.jsx';

const REFRESH_MS = 60 * 1000;

// Re-renders every minute so "Saved 5 minutes ago" stays true.
const useMinuteTick = () => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);
};

const TIPS = {
  autosave: 'Save the grid in this browser after every change. Untick it to save only when you click Save.',
  save: 'Save the grid in this browser now. Available when Autosave is off and you have unsaved changes.',
  load: 'Restore the grid saved in this browser. It replaces the grid on screen.',
  discard: 'Delete the grid saved in this browser. The grid on screen stays as it is.',
};

const tipId = (key) => `grid-snapshot-tip-${key}`;

// The tooltip text again for screen readers, which the hover-only tooltip never reaches.
const TipDescription = ({ name }) => <span id={tipId(name)} className="sr-only">{TIPS[name]}</span>;

TipDescription.propTypes = { name: PropTypes.string.isRequired };

const Separator = () => <span className="grid-snapshot-status-sep" aria-hidden="true">·</span>;

const savedGridShape = PropTypes.shape({
  savedAt: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
});

// "Saved grid · 12 rows · 2 hours ago", for both the grid saved on arrival and the last save.
const SavedGrid = ({ grid }) => (
  <span className="text-muted">
    <i className="icon-history position-left" aria-hidden="true" />
    Saved grid · {grid.rowCount} {grid.rowCount === 1 ? 'row' : 'rows'} · {timeAgo(grid.savedAt)}
  </span>
);

SavedGrid.propTypes = { grid: savedGridShape.isRequired };

// The saved-grid slot at the right end of the Grid Mode toolbar (#2633).
// Two rows: Autosave with the status, then Save / Load / Discard. The buttons
// are always there and disabled when they do not apply, so nothing jumps around.
export default function SnapshotStatus({
  offer, onLoad, onDiscard, autosave, onAutosaveChange, saved, dirty, onSaveNow,
}) {
  useMinuteTick();

  const showUnsaved = !offer && dirty;
  const shown = offer || saved;
  const status = showUnsaved ? 'Unsaved changes' : (!offer && saved && 'Saved');
  return (
    <div className="grid-snapshot-status">
      <div className="grid-snapshot-status-text">
        <Tooltip as="label" className="text-muted" text={TIPS.autosave}>
          <input
            type="checkbox"
            aria-describedby={tipId('autosave')}
            checked={autosave}
            onChange={(e) => onAutosaveChange(e.target.checked)}
          />
          Autosave
        </Tooltip>
        <TipDescription name="autosave" />
        {/* One persistent live region announces the state only. The relative
            time stays outside it so the minute tick is not announced. */}
        <span className="sr-only" role="status">{status}</span>
        {(showUnsaved || shown) && (
          <>
            <Separator />
            {showUnsaved ? <span className="text-muted">Unsaved changes</span> : <SavedGrid grid={shown} />}
          </>
        )}
      </div>
      <div className="grid-snapshot-status-actions">
        {/* "grid" stays in the accessible name to tell it apart from the form's Save */}
        <Tooltip text={TIPS.save}>
          <button
            type="button"
            className="btn btn-link btn-xs text-slate"
            aria-describedby={tipId('save')}
            disabled={!!offer || autosave || !dirty}
            onClick={onSaveNow}
          >
            <i className="icon-floppy-disk position-left" aria-hidden="true" />
            Save<span className="sr-only"> grid</span>
          </button>
        </Tooltip>
        <TipDescription name="save" />
        <Tooltip text={TIPS.load}>
          <button
            type="button"
            className="btn btn-link btn-xs text-info"
            aria-describedby={tipId('load')}
            disabled={!offer && !saved}
            onClick={onLoad}
          >
            <i className="icon-folder-open position-left" aria-hidden="true" />
            Load
          </button>
        </Tooltip>
        <TipDescription name="load" />
        <Tooltip text={TIPS.discard}>
          <button
            type="button"
            className="btn btn-link btn-xs text-danger"
            aria-describedby={tipId('discard')}
            disabled={!offer && !saved}
            onClick={onDiscard}
          >
            <i className="icon-trash position-left" aria-hidden="true" />
            Discard
          </button>
        </Tooltip>
        <TipDescription name="discard" />
      </div>
    </div>
  );
}

SnapshotStatus.propTypes = {
  offer: savedGridShape,
  onLoad: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  autosave: PropTypes.bool.isRequired,
  onAutosaveChange: PropTypes.func.isRequired,
  saved: savedGridShape,
  dirty: PropTypes.bool.isRequired,
  onSaveNow: PropTypes.func.isRequired,
};
